import { AnimatePresence } from 'framer-motion';
import { FC, memo, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import styled from 'styled-components';

import Button from '@/components/Button';
import useStorageToggle from '@/hooks/useStorageToggle';
import { OptimisationTestUnitResource } from '@/types/resources';
import {
  OptimisationProjectNetworkEnum,
  OptimisationTestTypeEnum,
} from '@/utils/enums';

import DeleteAllUnitsButton from './DeleteAllUnitsButton';
import GoogleAdsExportButton from './GoogleAdsExportButton';
import TestHeading from './TestHeading';
import TestsStatusHolder from './TestsStatusHolder';
import TipsFormModal from './TipsFormModal';
import TipsTable from './TipsTable';

const ButtonWrapper = styled.div`
  justify-content: flex-end;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  > button {
    padding-left: 16px;
    padding-right: 16px;
    font-weight: 600;
  }
`;

interface Props {
  tests?: OptimisationTestUnitResource[];
  network: OptimisationProjectNetworkEnum;
  externalProjectId: string;
  projectTitle: string;
}

const Tips: FC<Props> = memo(
  ({ tests, network, externalProjectId, projectTitle }) => {
    const [showTestModal, setShowTestModal] = useState(false);
    const [isOpen, toggleIsOpen] = useStorageToggle(
      `optimisation_test_${externalProjectId}_tips`,
    );

    const batches = useMemo(
      () =>
        tests!.reduce<number[]>((acc, curr) => {
          if (acc.includes(curr.batch)) {
            return acc;
          }

          return [...acc, curr.batch];
        }, []),
      [tests],
    );

    return (
      <>
        <TestHeading
          heading="Tips"
          description="Determines which clips keep viewers watching the longest"
          onToggle={toggleIsOpen}
          isOpen={isOpen}
        />
        {isOpen && (
          <>
            <DndProvider backend={HTML5Backend}>
              <TipsTable tests={tests} />
            </DndProvider>
            <ButtonWrapper>
              <TestsStatusHolder tests={tests!} />
              <GoogleAdsExportButton
                type={OptimisationTestTypeEnum.TIPS}
                externalProjectId={externalProjectId}
              />
              <DeleteAllUnitsButton
                type={OptimisationTestTypeEnum.TIPS}
                batches={batches}
              />
              <Button onClick={() => setShowTestModal(true)} $type="colorized">
                Upload tests
              </Button>
            </ButtonWrapper>
            <AnimatePresence>
              {showTestModal && (
                <TipsFormModal
                  projectTitle={projectTitle}
                  onClose={() => setShowTestModal(false)}
                  network={network}
                />
              )}
            </AnimatePresence>
          </>
        )}
      </>
    );
  },
);

export default Tips;
